import { useId } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import type {
  AutocompleteRenderInputParams,
  AutocompleteRenderValue,
  AutocompleteRenderValueGetItemProps,
} from '@mui/material/Autocomplete';
import OutlinedInput from '@mui/material/OutlinedInput';

import { getInputControlState, InputControl } from 'ui/forms/input-control';
import { mergeSx } from 'ui/merge-sx';
import {
  SELECT_DEFAULT_PLACEHOLDER,
  SELECT_DEFAULT_TEST_ID,
} from 'ui/select/select.constants';
import {
  getOptionKey,
  getOptionLabel,
  getSelectedSummary,
  getSelectPlaceholder,
  isSameOption,
  toOptionList,
} from 'ui/select/select.helpers';
import {
  getSelectControlSx,
  SelectChip,
  SelectVisuallyHidden,
} from 'ui/select/select.styles';
import type {
  SelectChangeValue,
  SelectOption,
  SelectProps,
} from 'ui/select/select.types';
import { useSelect } from 'ui/select/use-select';

import Icons from 'assets/icons';

const Select = <TOption extends SelectOption = SelectOption>({
  label,
  'aria-label': ariaLabel,
  options,
  value,
  onChange,
  onBlur,
  clearInputErr,
  id,
  name,
  placeholder = SELECT_DEFAULT_PLACEHOLDER,
  size = 'large',
  multiple = false,
  creatable = false,
  searchable = true,
  clearable = false,
  loading = false,
  disabled = false,
  required,
  error,
  errorMessage,
  helperText,
  disablePortal,
  ref,
  sx,
  controlSx,
  'data-testid': dataTestId = SELECT_DEFAULT_TEST_ID,
}: SelectProps<TOption>) => {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const isRequired = !!required && !disabled;
  const visibleHelperText = disabled ? undefined : helperText;
  const { isError, describedBy } = getInputControlState({
    id: fieldId,
    disabled,
    error,
    errorMessage,
    helperText: visibleHelperText,
  });
  const select = useSelect({
    options,
    value,
    onChange: onChange as
      | ((value: SelectChangeValue<SelectOption>) => void)
      | undefined,
    onBlur,
    clearInputErr,
    hasError: isError,
    multiple,
    creatable,
    searchable,
  });
  const selectedSummary = multiple ? getSelectedSummary(select.selected) : '';
  const summaryId = selectedSummary ? `${fieldId}-selected` : undefined;
  const inputDescribedBy =
    [describedBy, summaryId].filter(Boolean).join(' ') || undefined;
  const visiblePlaceholder = getSelectPlaceholder({
    placeholder,
    disabled,
    multiple,
    hasValue: select.hasValue,
    isTagInput: select.isTagInput,
  });

  const renderChips = (
    selectedValue: AutocompleteRenderValue<SelectOption, boolean, false>,
    getItemProps: AutocompleteRenderValueGetItemProps<boolean>
  ) =>
    toOptionList(selectedValue).map((option, index) => {
      const { key, ...itemProps } = (
        getItemProps as AutocompleteRenderValueGetItemProps<true>
      )({ index });

      return (
        <SelectChip
          key={key}
          {...itemProps}
          fieldSize={size}
          label={option.label}
          deleteIcon={<Icons.XinCircle aria-hidden="true" focusable="false" />}
        />
      );
    });

  const renderInput = ({
    slotProps: { input, htmlInput },
  }: AutocompleteRenderInputParams) => (
    <OutlinedInput
      ref={input.ref}
      className={input.className}
      startAdornment={input.startAdornment}
      endAdornment={input.endAdornment}
      onMouseDown={input.onMouseDown}
      fullWidth
      size={size}
      error={isError}
      name={name}
      placeholder={visiblePlaceholder}
      inputRef={ref}
      onBlur={select.handleBlur}
      slotProps={{
        input: {
          ...htmlInput,
          onMouseDown: select.getInputMouseDown(htmlInput.onMouseDown),
          readOnly: searchable ? htmlInput.readOnly : true,
          'aria-label': ariaLabel,
          'aria-required': isRequired || undefined,
          'aria-invalid': isError || undefined,
          'aria-describedby': inputDescribedBy,
        },
      }}
    />
  );

  return (
    <InputControl
      id={fieldId}
      size={size}
      label={label}
      required={isRequired}
      disabled={disabled}
      error={error}
      errorMessage={errorMessage}
      helperText={visibleHelperText}
      sx={sx}
      data-testid={dataTestId}
    >
      <Autocomplete<SelectOption, boolean, boolean, false>
        id={fieldId}
        data-testid={dataTestId}
        options={select.autocompleteOptions}
        value={select.selected}
        onChange={select.handleChange}
        inputValue={select.inputValue}
        onInputChange={select.handleInputChange}
        filterOptions={select.filterOptions}
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionKey}
        isOptionEqualToValue={isSameOption}
        onKeyDown={select.handleKeyDown}
        open={!select.isTagInput && select.isOpen}
        onOpen={select.handleOpen}
        onClose={select.handleClose}
        multiple={multiple}
        filterSelectedOptions={multiple}
        selectOnFocus={searchable}
        disableClearable={!clearable}
        clearOnEscape={clearable}
        forcePopupIcon={!disabled && !select.isTagInput}
        disabled={disabled}
        loading={loading}
        disablePortal={disablePortal}
        slotProps={{
          paper: {
            onMouseDown: (event: { stopPropagation: () => void }) =>
              event.stopPropagation(),
          },
        }}
        fullWidth
        popupIcon={<Icons.ChevronDown aria-hidden="true" focusable="false" />}
        clearIcon={<Icons.Close aria-hidden="true" focusable="false" />}
        sx={mergeSx(getSelectControlSx(size, multiple), controlSx)}
        renderInput={renderInput}
        {...(multiple ? { renderValue: renderChips } : {})}
      />
      {summaryId && (
        <SelectVisuallyHidden id={summaryId}>
          {selectedSummary}
        </SelectVisuallyHidden>
      )}
    </InputControl>
  );
};

export default Select;
